import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Dialog } from '@angular/cdk/dialog';
import { Subject, catchError, debounceTime, distinctUntilChanged, of, switchMap, takeUntil } from 'rxjs';
import { ContactAutocompleteResult, ContactAutocompleteService } from '../services/contacts/contact-autocomplete.service';
import { ReturnBatchInput, ReturnBatchItem, ReturnBatchOptions, ReturnProductRow, ReturnDetail, ReturnPreview, ReturnLading, ReturnLadingProduct } from './return-batch.types';
import { ReturnBatchesService } from './return-batches.service';
import { parseReturnCsv } from './return-csv';
import { ReturnBatchLabelsComponent } from './return-batch-labels.component';

@Component({
  selector: 'app-return-batches', templateUrl: './return-batches.component.html',
  styleUrls: ['../products/products.component.scss', './return-batches.component.scss']
})
export class ReturnBatchesComponent implements OnInit, OnDestroy {
  theme: 'dark' | 'light' = localStorage.getItem('products-theme') === 'light' ? 'light' : 'dark';
  options: ReturnBatchOptions | null = null;
  optionsLoading = true;
  list: ReturnDetail['batch'][] = [];
  total = 0; offset = 0; search = ''; customerFilter = '';
  createMode = false; detail: ReturnDetail | null = null;
  form = this.emptyForm();
  reviewCurrent = false; lookupRow: number | null = null; ladingCustomer = '';
  entryMode: 'new' | 'existing' | '' = '';
  ladingText = ''; ladingResults: ReturnLading[] = []; sourceLading: ReturnLading | null = null;
  ladingProducts: ReturnLadingProduct[] = []; selectedProducts = new Set<string>();
  ladingLoading = false; ladingError = ''; productFilter = '';
  private ladingVersion = 0;
  rows: ReturnProductRow[] = []; filename = ''; shipperText = ''; shipperResults: ContactAutocompleteResult[] = [];
  preview: ReturnPreview | null = null;
  selected = new Set<string>();
  loading = false; reviewing = false; saving = false; readingFile = false;
  pendingImport: { input: ReturnBatchInput; key: string } | null = null;
  error = ''; fileError = ''; shipperError = ''; success = '';
  private readonly destroyed = new Subject<void>();
  private readonly searchEvents = new Subject<void>();
  private readonly shipperEvents = new Subject<string>();
  private viewVersion = 0;
  private formVersion = 0;
  private fileVersion = 0;

  constructor(private readonly service: ReturnBatchesService, private readonly contacts: ContactAutocompleteService,
    private readonly route: ActivatedRoute, private readonly router: Router, private readonly dialog: Dialog) {}

  ngOnInit(): void {
    this.shipperEvents.pipe(debounceTime(250), distinctUntilChanged(), switchMap((q) =>
      this.contacts.search('shipper_contact_list', q).pipe(catchError(() => {
        this.shipperError = 'Shipper search is unavailable. Try again.'; return of([]);
      }))), takeUntil(this.destroyed)).subscribe((rows) => {
        this.shipperResults = this.form.shipper_contact_uuid ? [] : rows;
      });
    this.searchEvents.pipe(debounceTime(300), takeUntil(this.destroyed)).subscribe(() => this.loadList());
    this.route.paramMap.pipe(takeUntil(this.destroyed)).subscribe((params) => {
      this.viewVersion++;
      this.createMode = this.route.snapshot.data['create'] === true;
      this.detail = null; this.selected.clear(); this.error = ''; this.success = '';
      const uuid = params.get('uuid');
      if (uuid) { this.loadDetail(uuid); }
      else if (!this.createMode && this.options?.ready) { this.loadList(); }
    });
    this.loadOptions();
  }

  private emptyForm(): Omit<ReturnBatchInput, 'rows' | 'source_filename'> {
    const now = new Date();
    const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return { entry_mode: 'mixed', source_lading_uuid: null, customer_contact_uuid: '', shipper_contact_uuid: null, reference_number: '', return_date: date, condition: 2, notes: '' };
  }

  loadOptions(): void {
    this.optionsLoading = true; this.error = '';
    this.service.options().pipe(takeUntil(this.destroyed)).subscribe({
      next: (options) => {
        this.options = options; this.optionsLoading = false;
        if (options.ready && !this.createMode && !this.route.snapshot.paramMap.get('uuid')) { this.loadList(); }
      }, error: (e) => { this.optionsLoading = false; this.error = e.error?.message || 'Return batches could not load.'; }
    });
  }
  loadList(reset = false): void {
    if (!this.options?.ready) { return; }
    if (reset) { this.offset = 0; }
    const version = ++this.viewVersion;
    this.loading = true; this.error = '';
    this.service.list(this.search, this.customerFilter, this.offset).pipe(takeUntil(this.destroyed)).subscribe({
      next: (response) => { if (version !== this.viewVersion) { return; }
        this.list = response.batches; this.total = response.total; this.loading = false; },
      error: (e) => { if (version !== this.viewVersion) { return; }
        this.loading = false; this.error = e.error?.message || 'Saved batches could not load.'; }
    });
  }
  searchChanged(): void { this.offset = 0; this.searchEvents.next(); }
  page(delta: number): void { this.offset = Math.max(0, this.offset + delta * 50); this.loadList(); }
  loadDetail(uuid: string): void {
    const version = ++this.viewVersion;
    this.loading = true;
    this.service.detail(uuid).pipe(takeUntil(this.destroyed)).subscribe({
      next: (response) => { if (version !== this.viewVersion) { return; }
        this.detail = response; this.loading = false;
        this.selected = new Set(response.items.filter((i) => i.label_available === true).map((i) => i.product_uuid)); },
      error: (e) => { if (version !== this.viewVersion) { return; }
        this.loading = false; this.error = e.error?.message || 'This batch could not load.'; }
    });
  }
  changed(): void { this.reviewCurrent = false; this.formVersion++; this.preview = null; this.error = ''; }
  customerChanged(): void { this.resetProducts(); this.changed(); }
  modeChanged(): void {
    this.form.entry_mode = this.entryMode === 'existing' ? 'existing' : 'mixed';
    this.resetProducts(); this.changed();
  }
  private resetProducts(): void {
    this.fileVersion++; this.ladingVersion++;
    this.rows = []; this.filename = ''; this.readingFile = false; this.fileError = '';
    this.form.source_lading_uuid = null; this.sourceLading = null;
    this.ladingResults = []; this.ladingProducts = []; this.selectedProducts.clear();
    this.lookupRow = null; this.ladingCustomer = this.form.customer_contact_uuid;
    this.ladingText = ''; this.ladingError = ''; this.ladingLoading = false; this.productFilter = '';
  }
  searchLadings(): void {
    if (this.pendingImport || !this.form.customer_contact_uuid || this.ladingText.trim().length < 2) { return; }
    const version = ++this.ladingVersion;
    this.ladingLoading = true; this.ladingError = ''; this.ladingResults = [];
    this.service.ladings(this.ladingCustomer || this.form.customer_contact_uuid, this.ladingText.trim()).pipe(takeUntil(this.destroyed)).subscribe({
      next: (response) => {
        if (version !== this.ladingVersion) { return; }
        this.ladingLoading = false; this.ladingResults = response.ladings;
        if (!response.ladings.length) { this.ladingError = 'No ladings found for this customer. Try its lading number, reference or consignee.'; }
      }, error: (e) => { if (version !== this.ladingVersion) { return; }
        this.ladingLoading = false; this.ladingError = e.error?.message || 'Lading search could not load.'; }
    });
  }
  chooseLading(lading: ReturnLading): void {
    if (this.pendingImport) { return; }
    const version = ++this.ladingVersion;
    this.sourceLading = lading;
    if (this.lookupRow === null) { this.form.source_lading_uuid = lading.lading_uuid; }
    this.ladingResults = []; this.ladingProducts = []; this.selectedProducts.clear();
    if (this.lookupRow === null) { this.rows = []; }
    this.ladingError = ''; this.ladingLoading = true; this.markReviewStale();
    this.service.ladingProducts(this.ladingCustomer || this.form.customer_contact_uuid, lading.lading_uuid).pipe(takeUntil(this.destroyed)).subscribe({
      next: (response) => { if (version !== this.ladingVersion) { return; }
        this.ladingProducts = response.products; this.ladingLoading = false;
        if (!response.products.length) { this.ladingError = 'No current product records were found on this lading.'; }
      }, error: (e) => { if (version !== this.ladingVersion) { return; }
        this.ladingLoading = false; this.ladingError = e.error?.message || 'Lading products could not load.'; }
    });
  }
  get visibleLadingProducts(): ReturnLadingProduct[] {
    const q = this.productFilter.trim().toLowerCase();
    return this.ladingProducts.filter((p) => !q || [p.serial_number, p.model_number, p.package_id, p.barcode, p.product_uuid, p.description]
      .some((value) => (value || '').toLowerCase().includes(q)));
  }
  toggleReturnProduct(product: ReturnLadingProduct, checked: boolean): void {
    if (this.pendingImport) { return; }
    if (this.lookupRow !== null && checked) {
      const row = this.rows[this.lookupRow];
      row.inventory_action = 'existing'; row.existing_product_uuid = product.product_uuid;
      row.source_lading_uuid = this.sourceLading?.lading_uuid || null; row.transfer_customer = false;
      const reviewRow = this.preview?.items[this.lookupRow];
      if (reviewRow && !reviewRow.candidates.some((p) => p.product_uuid === product.product_uuid)) {
        reviewRow.candidates.push({ ...product, customer_contact_uuid: this.ladingCustomer || this.form.customer_contact_uuid,
          customer_name: this.options?.customers.find((c) => c.customer_contact_uuid === this.ladingCustomer)?.customer_name || null,
          source_lading_uuid: row.source_lading_uuid, source_reference: this.sourceLading?.alternate_reference || null });
      }
      this.lookupRow = null; this.markReviewStale(); return;
    }
    checked ? this.selectedProducts.add(product.product_uuid) : this.selectedProducts.delete(product.product_uuid);
    this.rows = this.ladingProducts.filter((p) => this.selectedProducts.has(p.product_uuid)).map((p) => ({
      serial_number: p.serial_number || '', model_number: p.model_number || '', existing_product_uuid: p.product_uuid, inventory_action: 'existing', source_lading_uuid: this.form.source_lading_uuid
    }));
    this.changed();
  }
  markReviewStale(): void { this.formVersion++; this.reviewCurrent = false; this.error = ''; }
  decisionChanged(index: number, action: string): void {
    const row = this.rows[index]; row.inventory_action = action === 'new' || action === 'existing' ? action : null;
    row.existing_product_uuid = null; row.source_lading_uuid = null; row.transfer_customer = false;
    this.markReviewStale();
  }
  markUnmatchedNew(): void {
    if (this.pendingImport || !this.preview) { return; }
    this.preview.items.forEach((item, index) => {
      if (!item.candidates.length && !this.rows[index].inventory_action) {
        this.rows[index].inventory_action = 'new';
      }
    });
    this.markReviewStale();
  }
  chooseExisting(index: number, uuid: string): void {
    const row = this.rows[index];
    const candidate = this.preview?.items[index].candidates.find((p) => p.product_uuid === uuid);
    row.inventory_action = 'existing'; row.existing_product_uuid = candidate?.product_uuid || null;
    row.source_lading_uuid = candidate?.source_lading_uuid || null; row.transfer_customer = false;
    this.markReviewStale();
  }
  needsCustomerTransfer(index: number): boolean {
    const row = this.rows[index];
    const candidate = this.preview?.items[index].candidates.find((p) => p.product_uuid === row.existing_product_uuid);
    return !!candidate && candidate.customer_contact_uuid !== this.form.customer_contact_uuid;
  }
  findOnLading(index: number): void {
    this.lookupRow = index; this.ladingCustomer = this.form.customer_contact_uuid;
    this.ladingText = ''; this.ladingResults = []; this.ladingProducts = []; this.sourceLading = null;
  }
  shipperChanged(value: string): void {
    this.form.shipper_contact_uuid = null; this.shipperResults = []; this.shipperError = '';
    this.changed(); this.shipperEvents.next(value);
  }
  chooseShipper(contact: ContactAutocompleteResult): void {
    this.form.shipper_contact_uuid = contact.value; this.shipperText = contact.label;
    this.shipperResults = []; this.shipperError = ''; this.changed();
  }
  async upload(event: Event): Promise<void> {
    if (this.pendingImport || this.entryMode !== 'new') { return; }
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const version = ++this.fileVersion;
    this.rows = []; this.filename = ''; this.fileError = ''; this.changed();
    if (!file) { return; }
    this.readingFile = true;
    try {
      if (!/\.csv$/i.test(file.name) || file.size > 131072) { throw new Error('Choose a CSV file up to 128 KB.'); }
      const content = new TextDecoder('utf-8', { fatal: true }).decode(await file.arrayBuffer());
      const rows = parseReturnCsv(content);
      if (version !== this.fileVersion) { return; }
      this.rows = rows; this.filename = file.name;
    } catch (e) {
      if (version === this.fileVersion) { this.fileError = e instanceof Error ? e.message : 'The CSV could not be read.'; }
    } finally { if (version === this.fileVersion) { this.readingFile = false; } }
  }
  template(): void {
    const url = URL.createObjectURL(new Blob(['serial_number,model_number\r\n'], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a'); a.href = url; a.download = 'return-products-template.csv'; a.click(); URL.revokeObjectURL(url);
  }
  get canReview(): boolean {
    return !!this.options?.ready && !!this.options?.can_import && !!this.form.customer_contact_uuid
      && !!this.entryMode && (this.entryMode !== 'existing' || !!this.form.source_lading_uuid)
      && !this.ladingLoading && !!this.form.return_date && this.rows.length > 0 && !this.readingFile && !this.reviewing && !this.pendingImport
      && (!this.shipperText.trim() || !!this.form.shipper_contact_uuid);
  }
  review(): void {
    if (!this.canReview) { return; }
    const version = this.formVersion;
    this.reviewing = true; this.error = ''; this.preview = null;
    this.service.preview({ ...this.form, source_filename: this.filename, rows: this.rows }).pipe(takeUntil(this.destroyed)).subscribe({
      next: (preview) => { this.reviewing = false; if (version === this.formVersion) { this.preview = preview; this.reviewCurrent = true; } },
      error: (e) => { this.reviewing = false; if (version === this.formVersion) { this.error = e.error?.message || 'The batch could not be reviewed.'; } }
    });
  }
  importBatch(): void {
    if (this.saving || (!this.pendingImport && (!this.preview?.valid || !this.reviewCurrent))) { return; }
    this.pendingImport ??= { input: structuredClone({ ...this.form, source_filename: this.filename, rows: this.rows,
      review_token: this.preview!.review_token }), key: 'return-' + crypto.randomUUID() };
    this.saving = true; this.error = '';
    this.service.import(this.pendingImport.input, this.pendingImport.key).pipe(takeUntil(this.destroyed)).subscribe({
      next: (detail) => {
        this.saving = false; this.pendingImport = null; this.preview = null; this.rows = []; this.filename = '';
        this.form = this.emptyForm(); this.shipperText = ''; this.entryMode = ''; this.resetProducts();
        this.router.navigate(['/return-batches', detail.batch.product_return_uuid]);
      }, error: (e) => {
        this.saving = false;
        if (e.status >= 400 && e.status < 500) {
          this.pendingImport = null; this.preview = e.error?.preview || null;
          this.error = e.error?.message || 'The import was rejected. Review again.';
        } else {
          this.error = 'The import result is unconfirmed. Retry the same import below; it will reuse the save ID.';
        }
      }
    });
  }
  get routeSnapshotUuid(): string | null { return this.route.snapshot.paramMap.get('uuid'); }
  condition(value: number): string { return this.options?.conditions[String(value)] || ({ 1: 'New', 2: 'Used', 3: 'Damaged', 4: 'Open Box' } as Record<number, string>)[value] || 'Unknown'; }
  toggle(item: ReturnBatchItem, checked: boolean): void {
    checked ? this.selected.add(item.product_uuid) : this.selected.delete(item.product_uuid);
  }
  get selectable(): ReturnBatchItem[] { return this.detail?.items.filter((i) => i.label_available === true) || []; }
  get allSelected(): boolean { return this.selectable.length > 0 && this.selectable.every((i) => this.selected.has(i.product_uuid)); }
  toggleAll(checked: boolean): void { this.selected = new Set(checked ? this.selectable.map((i) => i.product_uuid) : []); }
  printLabels(all = false): void {
    if (!this.detail) { return; }
    const ids = all ? this.selectable.map((i) => i.product_uuid) : [...this.selected];
    if (!ids.length) { return; }
    this.dialog.open(ReturnBatchLabelsComponent, {
      data: { uuid: this.detail.batch.product_return_uuid, ids, theme: this.theme },
      width: '860px', maxWidth: '96vw', ariaLabel: 'Return batch label preview'
    });
  }
  toggleTheme(): void { this.theme = this.theme === 'dark' ? 'light' : 'dark'; localStorage.setItem('products-theme', this.theme); }
  ngOnDestroy(): void { this.destroyed.next(); this.destroyed.complete(); this.fileVersion++; this.ladingVersion++; }
}
